import { deepClone, deleteAtPath, getAtPath, mergeDeep, omit, pick, setAtPath } from '@sitecore-search/common';
export default class RequestData {
    constructor() {
        Object.defineProperty(this, "listeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "props", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "dataSnapshot", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
    }
    takeSnapshot() {
        this.dataSnapshot = JSON.parse(JSON.stringify(this.data));
        return this;
    }
    restoreSnapshot() {
        this.data = JSON.parse(JSON.stringify(this.dataSnapshot));
        this.onChange('@restore-snapshot', null);
        return this;
    }
    set(prop, value, key) {
        if (prop in this.props) {
            const propKey = this.getPropExportKey(prop, key);
            const prevValue = this.get(prop, undefined);
            if (prevValue !== value) {
                const definition = this.props[prop];
                if (typeof value === 'undefined') {
                    this.removeKey(definition.parent?.key, definition.keyPath[0]);
                }
                else {
                    setAtPath(this.data, propKey, value);
                }
                this.onChange(prop, value);
            }
        }
        return this;
    }
    get(prop, defaultValue, key) {
        if (prop in this.props) {
            return getAtPath(this.data, this.getPropExportKey(prop, key), defaultValue);
        }
        return defaultValue;
    }
    resetValue(prop, key) {
        if (prop in this.props) {
            const definition = this.props[prop];
            const value = definition.initialValue ? definition.initialValue() : undefined;
            if (value === undefined && key && definition.parent && definition.parent.type === 'map') {
                return this.removeKey(definition.parent.key, key);
            }
            return this.set(prop, value, key);
        }
        return this;
    }
    getMapExportKey(keyPath, key) {
        return keyPath.map((value) => (value === '#KEY#' ? key : value));
    }
    getPropExportKey(prop, key) {
        if (prop in this.props) {
            const exportKey = this.props[prop].exportKey;
            if (key) {
                return this.getMapExportKey(exportKey, key).join('.');
            }
            return exportKey.join('.');
        }
        return '';
    }
    getValue(prop, defaultValue, key) {
        if (prop in this.props) {
            return this.get(prop, defaultValue, key);
        }
        return defaultValue;
    }
    setValue(prop, value, key) {
        if (prop in this.props) {
            if (this.props[prop].type === 'array' && value.length === 0) {
                return this.resetValue(prop, key);
            }
            return this.set(prop, value, key);
        }
        return this;
    }
    removeKey(prop, keyToRemove) {
        if (!prop) {
            this.data = omit(this.data, [keyToRemove]);
            return this;
        }
        let value = this.getValue(prop, {});
        deleteAtPath(value, typeof keyToRemove === 'string' ? keyToRemove : `[${keyToRemove}]`);
        if (Object.keys(value).length === 0) {
            value = undefined;
        }
        return this.setValue(prop, value);
    }
    updateValue(prop, value, key) {
        return this.setValue(prop, mergeDeep(this.getValue(prop, {}, key), value), key);
    }
    addValue(prop, value, key) {
        if (prop in this.props && this.props[prop].type === 'array') {
            return this.setValue(prop, this.getValue(prop, [], key).concat(value), key);
        }
        return this;
    }
    removeValue(prop, value, filter = (v) => v !== value, key) {
        if (prop in this.props && this.props[prop].type === 'array') {
            this.setValue(prop, this.getValue(prop, [], key).filter((v) => filter(v, value)), key);
        }
        return this;
    }
    load(request, triggerChange = false) {
        this.data = request.toJson();
        if (triggerChange) {
            this.onChange('load', 'load');
        }
        return this;
    }
    transformJson(json) {
        return json;
    }
    toJson(...props) {
        if (Object.keys(this.data).length === 0) {
            return this.transformJson(undefined);
        }
        if (props.length === 0) {
            return this.transformJson(deepClone(this.data));
        }
        return this.transformJson(pick(deepClone(this.data), props));
    }
    onChange(prop, value) {
        this.listeners.forEach((listener) => {
            listener.onChange(this, prop, value);
        });
    }
    addListeners(observers) {
        observers.forEach((observer) => this.addListener(observer));
    }
    addListener(observer) {
        if (!this.listeners.find((listener) => listener === observer)) {
            this.listeners.push(observer);
        }
    }
    removeListener(observer) {
        this.listeners = this.listeners.filter((listener) => listener !== observer);
    }
}
